import { isPaymentTermDaysValid, MAX_INPUT_LENGTH, MAX_VENDOR_NAME_EXTENDED_LENGTH, MAX_VENDOR_NAME_LENGTH } from "@finway-group/shared/lib/utils/validators"
import { MAX_PAYMENT_TERM_DAYS, MIN_PAYMENT_TERM_DAYS } from "@finway-group/shared/lib/utils/validators/vendor/vendorValidationConsts"
import { type Rule } from "antd/lib/form"
import { isValidPhoneNumber } from "react-phone-number-input"
import { generalRules } from "~Components/forms/rules/common/general.rules"
import { stringRules } from "~Components/forms/rules/common/string.rules"
import { i18n } from "~Shared/locales/i18n"
import { isSet, isValidBIC, isValidIBAN } from "~Shared/utils/helper.utils"
import { validateEmail } from "~Shared/validators/email.validator"

export const createVendorRules = () => ({
    name: [
        generalRules.required,
        stringRules.noExcessWhitespace,
        // DATEV limit
        stringRules.maxLength(MAX_VENDOR_NAME_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    nameExtended: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_VENDOR_NAME_EXTENDED_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    city: [
        stringRules.noExcessWhitespace,
        // TODO why is this 30 when MAX_VENDOR_CITY_LENGTH is 50?
        stringRules.maxLength(30),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    creditorNumber: [stringRules.allowedMultiLineCharsOnly] as Array<Rule>,
    streetName: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_INPUT_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    streetNumber: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_INPUT_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    country: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_INPUT_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    countryCode: [
        {
            validator: (_rule, value: string) => {
                if (!value || /^[A-Z]{2}$/.test(value)) {
                    return Promise.resolve()
                }
                return Promise.reject(new Error(i18n.t("validation:exact.string", { num: 2 })))
            },
        },
    ] as Array<Rule>,
    zip: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_INPUT_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    description: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_INPUT_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    rating: [{ type: "integer", required: true, message: i18n.t("validation:required") }] as Array<Rule>,
    recommendation: [{ type: "integer", required: true, message: i18n.t("validation:required") }] as Array<Rule>,
    contactPersonName: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_INPUT_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    contactPersonEmail: [
        {
            validator: (_rule, value: string) => {
                if (!validateEmail(value) && value) {
                    return Promise.reject(new Error(i18n.t("validation:email")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    contactPersonPhone: [
        {
            validator: (_rule, value: string) => {
                if (value && !isValidPhoneNumber(value)) {
                    return Promise.reject(new Error(i18n.t("validation:phone")))
                }
                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    iban: [
        {
            validator: (_rule, value: string) => {
                if (value && value !== "" && !isValidIBAN(value)) {
                    return Promise.reject(new Error(i18n.t("validation:iban")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    bic: [
        {
            validator: (_rule, value: string) => {
                if (value && value !== "" && !isValidBIC(value)) {
                    return Promise.reject(new Error(i18n.t("validation:bic")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    vatNumber: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_INPUT_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    taxNumber: [
        // keep line breaks
        stringRules.noExcessWhitespace,
        stringRules.maxLength(MAX_INPUT_LENGTH),
        stringRules.allowedMultiLineCharsOnly,
    ] as Array<Rule>,
    paymentTerm: [
        {
            validator: (_rule, value?: number) => {
                if (!isSet(value)) {
                    return Promise.resolve()
                }

                if (!Number.isInteger(Number(value))) {
                    return Promise.reject(new Error(i18n.t("validation:integer")))
                }

                if (!isPaymentTermDaysValid(value)) {
                    return Promise.reject(new Error(i18n.t("validation:numeric_min_max", { min: MIN_PAYMENT_TERM_DAYS, max: MAX_PAYMENT_TERM_DAYS })))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
})
